import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import * as tokens from '@trp-rps-rps-shared-web/rps-shared-web-styles';
import { Button, CollapsibleHedge } from '@trp-rps-rps-shared-web/rps-shared-web-components';
import AccountCard from './AccountCard';
import HsaCard from './HsaCard';
import ReactModalFooter from '../../Modal/ReactModalFooter';
import ReactModalHeader from '../../Modal/ReactModalHeader';
import ReactModalContent from '../../Modal/ReactModalContent';
import { modalActions, accountActions, chartsActions } from '../../../redux/root-action';
import {
  modals,
  accountModalViews,
  derivedAttributes,
  postActions,
  units,
  frequency,
  accountTypeCodes,
  analyticsTracking,
  contributionTypes,
} from '../../../utils/constants';
import { formatAmountWithoutDecimals, keyDown } from '../../../utils/helpers';
import { isInternal } from '../../../utils/api';

const OutsideAccounts = styled.div`
  background-color: ${tokens.ColorWhite025};
  margin: 0px 0px 8px 0px;
  padding: 24px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: baseline;
  @media (max-width: 768px) {
    padding: 16px;
    margin: 0px 0px 8px 0px;
    flex-direction: column;
  }
`;

const OutsideAccountsButtonContainer = styled.div`
  display: flex;
`;

const HsaContainer = styled.div`
  display: flex;
  justify-content: center
  align-items: center
`;

const OutsideAccountsHeader = styled.div`
  flex: 1;
  font-family: Nimbus Sans Bold;
  font-size: 16px;
  line-height: 22px;
  padding-top: 5px;
  color: ${tokens.ColorGrey800};
`;

const CurrentBalanceContainer = styled.div`
  margin: 32px 0px 24px 0px;
  @media (max-width: 768px) {
    margin: 24px 0px 16px;
  }
`;

const CurrentBalance = styled.div`
  font-family: Nimbus Sans Bold;
  font-size: 24px;
  letter-spacing: -0.5px;
  color: ${tokens.ColorGrey900};
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CurrentBalanceAmount = styled.div`
  float: right;
  font-family: Nimbus Sans Bold;
  font-size: 24px;
  letter-spacing: -0.5px;
  color: ${tokens.ColorGrey900};
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const AccountsContent = styled.div`
  margin: 0 0px 32px;
  @media (max-width: 768px) {
    margin: 0 0px 16px;
  }
`;

const TRPAccountsHeader = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  color: ${tokens.ColorGrey600};
  margin-bottom: 8px;
`;

const OtherAccountsHeader = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  color: ${tokens.ColorGrey600};
  margin: 44px 0 8px;
`;

const StyledDivider = styled.div`
  border-bottom: 1px solid ${tokens.ColorWhite100};
  margin: 0px 0px 0px 0px;
  @media only screen and (max-width: 767px) {
    margin: 0px;
  }
`;

const collapsibleText =
  "Mutual fund share prices and portfolio unit prices are calculated as of the close of trading of each business day and are generally uploaded to our website each business day between 6:00-7:00 PM ET, at which time current account balances should reflect that updated price information. Transactions placed during the current business day for your account will be processed overnight and should be available for viewing in the next morning's account information. Intra-day Brokerage trading activity and pending account activity will not be reflected in your current balance. Balances and prices for linked external accounts and other third-party data are subject to the timing of the respective providers. Balances for manually entered external accounts reflect the most recent manual entry or update.";

class YourAccountsModal extends Component<any, any> {
  cleanUpObjects = (objArr: Array<Record<any, any>>) => {
    return objArr.map((obj: Record<any, any>) => {
      let cleanedObject = {};
      Object.keys(obj).forEach(key => {
        if (!derivedAttributes.includes(key)) {
          cleanedObject = { ...cleanedObject, [key]: obj[key] };
        }
      });
      return cleanedObject;
    });
  };
  handleDone = () => {
    const { hideModal, postSimulation, updatedAccounts, riScenario, accountHolderOptions } = this.props;
    const tempAccounts: Array<Record<string, any>> = [],
      tempEvents: Array<Record<string, any>> = [];

    updatedAccounts.forEach((account: any) => {
      const {
        bpId,
        name,
        typeName,
        amount,
        startNumber,
        action,
        id,
        assetAllocation,
        contribution,
        annualSavingsEE,
        annualSavingsER,
        balance,
        isYodleeAccount,
        accountHolder,
        isOverriddenYodleeAccount,
        contributionType,
        includeInScenario,
      } = account;
      if (account.overrideAccountTypeCode === accountTypeCodes.PENSION) {
        tempEvents.push({
          id,
          bpId: bpId ? bpId : accountHolderOptions.find((option: any) => option.name === accountHolder).value,
          scenarioId: riScenario.scenarioId,
          name: name,
          typeName,
          categoryName: 'Pension',
          amount: Number(amount),
          startNumber: Number(startNumber),
          endNumber: Number(startNumber),
          includeInScenario: true,
          unit: units.AGE,
          frequency: typeName.includes('Monthly') ? frequency.MONTHLY : frequency.ONETIME,
          action: action ? action : postActions.UPDATE,
        });
      } else if (isYodleeAccount) {
        tempAccounts.push({
          ...account,
          action: isOverriddenYodleeAccount ? postActions.UPDATE : postActions.ADD,
          bpId: bpId ? bpId : accountHolderOptions.find((option: any) => option.name === accountHolder).value,
          assetAllocation: assetAllocation
            ? {
              stockPercent: assetAllocation.stockPercent / 100,
              bondsPercent: assetAllocation.bondsPercent / 100,
              moneyMarketPercent: assetAllocation.moneyMarketPercent / 100,
              otherPercent: assetAllocation.otherPercent / 100,
            }
            : null,
          balance: undefined,
          name: undefined,
        });
      } else {
        tempAccounts.push({
          ...account,
          action: action ? action : postActions.UPDATE,
          bpId: bpId ? bpId : accountHolderOptions.find((option: any) => option.name === accountHolder).value,
          assetAllocation: assetAllocation
            ? {
              stockPercent: assetAllocation.stockPercent / 100,
              bondsPercent: assetAllocation.bondsPercent / 100,
              moneyMarketPercent: assetAllocation.moneyMarketPercent / 100,
              otherPercent: assetAllocation.otherPercent / 100,
            }
            : null,
          balance: Number(balance),
          contribution: contribution
            ? contribution.map((contribution: any) => {
              if (contribution.id === 'EE') {
                return {
                  ...contribution,
                  amount: parseInt(annualSavingsEE),
                };
              }
              if (contribution.id === 'ER') {
                return {
                  ...contribution,
                  amount: parseInt(annualSavingsER),
                };
              }
              return contribution;
            })
            : contributionType === contributionTypes.BOTH
              ? [
                {
                  id: 'EE',
                  sourceName: '*',
                  amount: parseInt(annualSavingsEE),
                  unit: units.DOLLAR,
                  frequency: frequency.ANNUAL,
                },
                {
                  id: 'ER',
                  sourceName: '*',
                  amount: parseInt(annualSavingsER),
                  unit: units.DOLLAR,
                  frequency: frequency.ANNUAL,
                },
              ]
              : contributionType === contributionTypes.NONE
                ? []
                : contributionType === contributionTypes.ER
                  ? [
                    {
                      id: 'ER',
                      sourceName: '*',
                      amount: parseInt(annualSavingsER),
                      unit: units.DOLLAR,
                      frequency: frequency.ANNUAL,
                    },
                  ]
                  : [
                    {
                      id: 'EE',
                      sourceName: '*',
                      amount: parseInt(annualSavingsEE),
                      unit: units.DOLLAR,
                      frequency: frequency.ANNUAL,
                    },
                  ],
        });
      }
    });

    if (tempAccounts.length || tempEvents.length) {
      if (tempAccounts.length && tempEvents.length) {
        postSimulation({
          accounts: this.cleanUpObjects(tempAccounts),
          events: this.cleanUpObjects(tempEvents),
        });
      } else if (tempAccounts.length) {
        postSimulation({
          accounts: this.cleanUpObjects(tempAccounts),
        });
      } else if (tempEvents.length) {
        postSimulation({
          events: this.cleanUpObjects(tempEvents),
        });
      }
    }
    document.getElementsByTagName('html')[0].classList.remove('fixed');
    document.getElementsByTagName('html')[0].removeAttribute('style');
    hideModal();
  };

  handleCloseIconClick = () => {
    const {
      hideModal,
      updateAccountsData,
      modelledScenario: { accounts },
    } = this.props;
    updateAccountsData({ accounts });
    document.getElementsByTagName('html')[0].classList.remove('fixed');
    document.getElementsByTagName('html')[0].removeAttribute('style');
    hideModal();
  };

  showAddAccountModal = () => {
    const {
      updateAccountsData,
      showModal,
      setCurrentView,
      setSelectedAccount,
      accounts,
      riScenario: { participantProfile },
    } = this.props;
    const newAccount = {
      accountIndex: accounts.length,
      name: null,
      overrideAccountTypeCode: '',
      contributionType: 'None',
      balance: null,
      accountHolder: `${participantProfile.firstName} ${participantProfile.lastName}`,
      annualSavingsEE: 0,
      annualSavingsER: 0,
      assetAllocation: {
        stockPercent: 0,
        bondsPercent: 0,
        moneyMarketPercent: 0,
        otherPercent: 0,
      },
      action: postActions.ADD,
      displayType: 'Pension – Monthly',
      typeName: 'Pension – Monthly',
      isPensionAccount: false,
      isYodleeAccount: false,
      isNewAccount: true,
    };
    const tempAccounts = [...accounts];
    tempAccounts.push(newAccount);
    updateAccountsData({ accounts: tempAccounts });
    setSelectedAccount(newAccount);
    showModal(modals.ACCOUNT);
    setCurrentView(accountModalViews.ADD_ACCOUNT);
  };

  getCurrentBalance = () => {
    const { accounts, updatedAccounts } = this.props;
    let balance = '';

    balance = accounts.reduce((accum: number, account: any) => {
      const balance =
        account.overrideAccountTypeCode && account.overrideAccountTypeCode === accountTypeCodes.PENSION
          ? 0
          : account.balance;
      return accum + Number(balance);
    }, 0);

    for (let i = 0; i < updatedAccounts.length; i++) {
      if (
        updatedAccounts[i].action === 'DELETE' &&
        updatedAccounts[i].overrideAccountTypeCode &&
        updatedAccounts[i].overrideAccountTypeCode === accountTypeCodes.PENSION
      ) {
        balance = '' + (parseInt(balance) - parseInt(updatedAccounts[i].amount));
      } else if (updatedAccounts[i].action === 'DELETE') {
        balance = '' + (parseInt(balance) - parseInt(updatedAccounts[i].balance));
      }
    }

    return formatAmountWithoutDecimals(balance);
  };

  getDisplayType = ({ displayType, overrideAccountTypeCode }: any) => {
    const { accountTypes } = this.props;
    const filteredAccountType = accountTypes.data.find(
      (accountType: any) => accountType.value === overrideAccountTypeCode,
    );
    return filteredAccountType
      ? filteredAccountType.name === accountTypeCodes.PENSION
        ? displayType
        : filteredAccountType.name
      : displayType;
  };

  getContributionType = ({ overrideAccountTypeCode }: any) => {
    const { accountTypes } = this.props;
    const filteredAccountType = accountTypes.data.find(
      (accountType: any) => accountType.value === overrideAccountTypeCode,
    );

    return filteredAccountType
      ? filteredAccountType.name === accountTypeCodes.PENSION
        ? 'None'
        : filteredAccountType.contributionType
      : 'None';
  };

  showSelectedAccountInformation = (account: any) => {
    const { setCurrentView, setSelectedAccount, showModal } = this.props;
    setSelectedAccount({
      ...account,
      displayType: this.getDisplayType(account),
      contributionType: this.getContributionType(account),
    });
    showModal(modals.ACCOUNT);
    setCurrentView(accountModalViews.ACCOUNT_INFO);
  };

  goToYodlee = () => {
    const { showModal } = this.props;
    showModal(modals.YODLEE_REDIRECT);
  };

  render(): JSX.Element {
    const { accounts, setCurrentView, setSelectedAccount, isLoading } = this.props;
    const troweRetirementAccounts = accounts
      ? accounts.filter((ac: any) => ac.displayGroup === 'T. Rowe Price Retirement Accounts')
      : [];
    let sortedTroweRetirementAccounts: any = troweRetirementAccounts;
    const rpsAccounts: any = [];
    const definedBenefitAccounts: any = [];
    const retailAccounts: any = [];
    const remainingTRoweAccounts: any = [];
    troweRetirementAccounts.forEach((ac: any) => {
      if (ac.accountCategory === 'RPSAccountSummary' && ac.accountCategory !== 'HSAAccountSummary') {
        rpsAccounts.push(ac);
      } else if (ac.accountCategory === 'DefinedBenefitAccountSummary' && ac.accountCategory !== 'HSAAccountSummary') {
        definedBenefitAccounts.push(ac);
      } else if (ac.accountCategory === 'RetailAccountSummary' && ac.accountCategory !== 'HSAAccountSummary') {
        retailAccounts.push(ac);
      } else {
        remainingTRoweAccounts.push(ac);
      }
    });
    sortedTroweRetirementAccounts = [
      ...rpsAccounts,
      ...definedBenefitAccounts,
      ...retailAccounts,
      ...remainingTRoweAccounts,
    ];
    const outsideRetirementAccounts = accounts
      ? accounts.filter((ac: any) => (ac.displayGroup === 'Outside Retirement Accounts' || ac.isNewAccount) && ac.categoryName !== 'Pension')
      : [];
    if (!accounts) {
      return (
        <ReactModalHeader
          modalTitle="Your Accounts"
          showClose={true}
          handleCloseIconClick={this.handleCloseIconClick}
        />
      );
    }
    return (
      <React.Fragment>
        <ReactModalHeader
          modalTitle="Your Accounts"
          showClose={true}
          handleCloseIconClick={this.handleCloseIconClick}
        />
        <ReactModalContent>
          <CurrentBalanceContainer id={`account-balance-containter`}>
            <CurrentBalanceAmount id={`account-balance-amount`}>{this.getCurrentBalance()}</CurrentBalanceAmount>
            <CurrentBalance id={`account-balance`}>Current Balance</CurrentBalance>
            <CollapsibleHedge
              id={`account-collaps-hedge`}
              title={'Information About As Of Dates'}
              bodyCopy={collapsibleText}
            />
            <StyledDivider />
          </CurrentBalanceContainer>
          <AccountsContent id={`account-content`}>
            <TRPAccountsHeader id={`account-trp-header`}>Workplace Retirement Accounts</TRPAccountsHeader>
            {sortedTroweRetirementAccounts.map((account: any, i: number) => (
              (
                <>
                  {account.type !== "Hsa" ?
                    <AccountCard data={account} page="accountCard" key={i} id={'workplace-account-'+i} isTrpAccount={true} />
                    :
                    <>
                      <AccountCard
                        data={{
                          ...account,
                          displayType: this.getDisplayType(account),
                        }}
                        page="hsaCard"
                        key={i}
                        id={i}
                        isTrpAccount={true}
                        setCurrentView={setCurrentView}
                        setSelectedAccount={setSelectedAccount}
                      />
                    </>
                  }
                </>
              )
            ))}
            
            { outsideRetirementAccounts.length > 0 && (
                 <OtherAccountsHeader id={`account-other-header`}>Other Accounts</OtherAccountsHeader>
            )}           
            <OutsideAccounts id={`account-outside`}>              
              <OutsideAccountsHeader id={`account-outside-header`}>
                Add outside accounts
              </OutsideAccountsHeader>
              <OutsideAccountsButtonContainer id={`account-outside-button`}>
                <Button
                  type="secondary"
                  title="Link Account"
                  disabled={isInternal()}
                  onClick={this.goToYodlee}
                  tabIndex={0}
                  onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.goToYodlee)}
                  id={analyticsTracking.RIXYourAccountModalLinkAccount}
                />
                <Button
                  type="secondary"
                  title="Manually Enter"
                  onClick={this.showAddAccountModal}
                  tabIndex={0}
                  onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => keyDown(e, this.showAddAccountModal)}
                  marginLeftPx={16}
                  id={analyticsTracking.RIXYourAccountModalManuallyEnter}
                />
              </OutsideAccountsButtonContainer>
            </OutsideAccounts>
            {outsideRetirementAccounts.map(
              (account: any, i: number) =>
                account.action !== postActions.DELETE &&
                account.type !== "Hsa" &&
                (
                  <>
                    <AccountCard
                      data={{
                        ...account,
                        displayType: this.getDisplayType(account),
                      }}
                      page="accountCard"
                      key={i}
                      id={'outside-account-'+i}
                      onClick={() => this.showSelectedAccountInformation(account)}
                      onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) =>
                        keyDown(e, () => this.showSelectedAccountInformation(account))
                      }
                      setCurrentView={setCurrentView}
                      setSelectedAccount={setSelectedAccount}
                    />
                  </>
                ),
            )}

            {outsideRetirementAccounts.map(
              (account: any, i: number) =>
                account.action !== postActions.DELETE
                && account.type === "Hsa"
                && (
                  <>
                    <HsaContainer>
                      <HsaCard
                        data={{
                          ...account,
                          displayType: this.getDisplayType(account),
                        }}
                        page="hsaCard"
                        key={i}
                        id={i}
                        onClick={() => this.showSelectedAccountInformation(account)}
                        onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => () => this.showSelectedAccountInformation(account)}
                        setCurrentView={setCurrentView}
                        setSelectedAccount={setSelectedAccount}
                      />
                    </HsaContainer>
                  </>
                ),
            )}
          </AccountsContent>
        </ReactModalContent>
        <ReactModalFooter
          CTAButtonText="Done"
          handleCTAClick={this.handleDone}
          hideCancel={true}
          disabled={isInternal() || isLoading}
          CTAButtonId={analyticsTracking.RIXYourAccountModalDone}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({
  account: { currentPage, modelledScenario, tempScenario, accountHolderOptions },
  riScenario,
  charts,
  accountTypes,
}: any) => ({
  accounts: tempScenario.accounts,
  currentPage,
  modelledScenario,
  updatedAccounts: tempScenario.accounts?.filter((account: any) => account.action),
  riScenario,
  accountTypes,
  isLoading: riScenario.loading || charts.loading,
  accountHolderOptions,
});

export default connect(mapStateToProps, {
  setCurrentView: accountActions.setCurrentView,
  showModal: modalActions.showModal,
  hideModal: modalActions.hideModal,
  setSelectedAccount: accountActions.setSelectedAccount,
  updateAccountsData: accountActions.updateAccountsData,
  postSimulation: chartsActions.postSimulation,
})(YourAccountsModal);
